import { Calendar } from "@msuite/ui/calendar";
import { Panels } from "@msuite/ui/panels";
import { guardArray } from "@msuite/utils/array";
import { moment } from "@msuite/utils/date";
import type { AbsenceListItem, AttendanceListItem } from "@msuite/utils/types";
import { useMemo } from "react";
import { useUserAbsences } from "@/hooks/use-user-absences";
import type { UserPanelBaseProps } from "../types";
import { useUserAttendances } from "../user-attendances/use-user-attendances";
import { UserCalendarAbsenceItem } from "./user-calendar-absence-item";
import { UserCalendarAttendanceItem } from "./user-calendar-attendance-item";

type UserCalendarItemData =
	| { kind: "absence"; item: AbsenceListItem }
	| { kind: "attendance"; item: AttendanceListItem };

/** Props Interface */
interface UserCalendarProps extends UserPanelBaseProps {
	startDate: string;
	endDate: string;
}

export function UserCalendar({ startDate, endDate, userId }: UserCalendarProps) {
	/** Query */
	const { attendances } = useUserAttendances(userId, { startDate, endDate });
	const { absences } = useUserAbsences(userId, {}, moment(startDate).year());

	const data = useMemo(() => {
		return moment()
			.rangeDatesDb(startDate, endDate)
			.map((date) => {
				const absenceItems = guardArray(
					absences?.filter((item) =>
						moment(date).isBetween(item.startDate, item.endDate, "day", "[]"),
					),
				).map((item) => ({
					id: item.id,
					durationType: "all-day" as const,
					startTime: moment(item.startDate).startOf("day").toISOString(),
					endTime: moment(item.endDate).endOf("day").toISOString(),
					firstDayIsHalfDay: item.halfDayStart,
					lastDayIsHalfDay: item.halfDayEnd,
					_item: { kind: "absence" as const, item },
				}));

				const attendanceItems = guardArray(
					attendances?.filter(
						(item) => moment(item.startTime).isSame(date, "day") && item.type === "work",
					),
				).map((item) => ({
					id: item.id,
					durationType: "timed" as const,
					firstDayIsHalfDay: false,
					lastDayIsHalfDay: false,
					startTime: item.startTime,
					endTime: item.endTime ?? moment(item.startTime).endOf("day").toISOString(),
					_item: { kind: "attendance" as const, item },
				}));

				return {
					date,
					items: [...absenceItems, ...attendanceItems],
				};
			});
	}, [absences, attendances]);

	/** Render */
	return (
		<Panels>
			<Panels.Panel>
				<Calendar<UserCalendarItemData>
					initialDate={moment().subtract(4, "days").db()}
					data={data}
					getItemColor={(item) => {
						if (item.durationType === "timed") {
							if ("type" in item._item.item) {
								if (item._item.item.type === "break") return "gray";
							}
							return "blue";
						}
						if (item.durationType === "all-day") return "emerald";
						return "gray";
					}}
					renderItemContent={(item) => {
						if (item._item.kind === "absence")
							return <UserCalendarAbsenceItem item={item._item.item} />;
						return <UserCalendarAttendanceItem item={item._item.item} />;
					}}
					onItemClick={(_item) => {}}
				/>
			</Panels.Panel>
		</Panels>
	);
}
